(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD
    define(['underscore',
            'jquery',
            'torso/modules/ServiceCell',
            './userModel',
            './adminUserModel',
            'app/demographics/demographicsModel',
            './messageCountsModel',
            'services/loginStatusExtService',
            'app/visit/VisitModel'], factory);
  } else if (typeof exports === 'object') {
    // Node, CommonJS-like
    var urls = require('./../urls');
    module.exports = factory(
      require('underscore'),
      require('jquery'),
      require('backbone-torso/modules/ServiceCell'),
      require('./userModel'),
      require('./adminUserModel'),
      require('./../demographics/demographicsModel'),
      require('./messageCountsModel'),
      urls.loginStatusExtService,
      require('./../visit/VisitModel'));
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function (_, $, TorsoService, UserModel, AdminUserModel, DemographicsModel, MessageCountsModel,
                  loginStatusExtService, VisitModel) {
  'use strict';

  /**
   * Singleton service for managing models related to user information
   *
   * @class userService
   * @static
   * @extends Torso.ServiceCell
   */
  return new (TorsoService.extend({

    /**
     * Setup the models associated with the current user. These are implicitly singletons and should not be modified by services or
     * view.
     *
     * NOTE: When adding new models to the service, make sure they are appropriately handled by _setModelDefaults,
     * fetchModels, and resetAndFetchModels
     *
     * NOTE: Do not remove the fetchUserModel function, that IS being used. Do a search and see!
     *
     * @method initialize
     */
    initialize: function() {
      _.bindAll(this, 'fetchModels',
                      '_fetchAllModels',
                      '_setFetching',
                      '_setFetchingComplete',
                      '_setFetched',
                      'fetchUserModel',
                      '_fetchCurrentUserModel',
                      '_fetchCurrentAdminUserModel',
                      'isAuthenticated',
                      'resetAndFetchModels',
                      '_fetchPortalUserModels');
      this.set('authenticated', false);
      this.currentUserModel = new UserModel();
      this.currentAdminUserModel = new AdminUserModel();
      this.currentDemographicsModel = new DemographicsModel();
      var initialCurrentVisitAttributes = {};
      this.currentVisitModel = new VisitModel(initialCurrentVisitAttributes, {currentVisit: true});
      this.messageCountsModel = new MessageCountsModel();
      this._setModelDefaults();
    },

    /**
     * @return {Boolean} whether the user has been authenticated.
     */
    getCurrentAuthentication: function() {
      return this.get('authenticated');
    },

    /**
     * Setup default attributes and values for service models
     *
     * @method _setModelDefaults
     * @private
     */
    _setModelDefaults: function() {
      this.set('authenticated', false);
      this.currentUserModel.set({ username: 'current~' });
      this.currentAdminUserModel.set({username: 'current~'});
      this.currentDemographicsModel.set({ vecnaId: 'current' });
    },

    /**
     * Fetch all of the models on the user service if they have not already been fetched
     *
     * @method fetchModels
     * @return {Promise} Returns a promise object that resolves when all models finish fetching
     */
    fetchModels: function() {
      if (!this.get('fetched') && !this.get('fetching')) {
        this._setFetching();
        this._fetchModelsPromise = this.isAuthenticated()
          .then(this._fetchAllModels)
          .done(this._setFetched)
          .always(this._setFetchingComplete);
        // Fetching the user model is part of fetching all models so if one exist and this service is in the process
        // of fetching ('fetched' === true or 'fetching' === true) then we want to use the resolve of the promise to
        // know when the user model has completed fetching.
        this._fetchUserModelPromise = this._fetchModelsPromise;
      }
      return this._fetchModelsPromise;
    },

    /**
     * Private helper that does the actual fetch of the models.
     * Attempts to be smart about fetching patient related models only if the user is a patient.
     *
     * @method _fetchAllModels
     * @return {Promise}
     * @private
     */
    _fetchAllModels: function() {
      return $.when(this._fetchCurrentUserModel(),
        this.currentVisitModel.fetch())
        .then(this._fetchPortalUserModels);
    },

    /**
     * Fetch current demographics and message counts if the current user is a portal user.
     * @method _fetchPortalUserModels
     * @return {undefined|Promise} undefined if not portal user (no add'l fetches) or a promise that will resolve when
     *                             the fetches of add'l data is complete.
     * @private
     */
    _fetchPortalUserModels: function() {
      if (this.currentUserModel.get('@class') === '.PortalUserDTO') {
        return $.when(this.currentDemographicsModel.fetch(), this.messageCountsModel.fetch());
      } else if(this.currentUserModel.get('@class') === '.UserDTO') {
        return this._fetchCurrentAdminUserModel();
      }
    },

    /**
     * Sets the state of this service to be in the process of fetching (to block additional requests until it completes).
     *
     * @method _setFetching
     * @private
     */
    _setFetching: function() {
      this.set('fetching', true);
    },

    /**
     * Sets the state of this service to not be in the process of fetching (to unblock subsequent requests).
     *
     * @method _setFetchingComplete
     * @private
     */
    _setFetchingComplete: function() {
      this.set('fetching', false);
    },

    /**
     * Sets the state of this service to have fetched the models since the last reset.  Future requests to fetch will
     * be ignored.
     *
     * @method _setFetchingComplete
     * @private
     */
    _setFetched: function() {
      this.set('fetched', true);
    },

    /**
     * Fetch the user model on the user service if it has not already been fetched
     *
     * @method fetchUserModel
     * @return {Promise} Returns a promise object that resolves when all models finish fetching
     */
    fetchUserModel: function() {
      if (!this.get('fetched') && !this.get('fetching')) {
        this._setFetching();
        this._fetchUserModelPromise = this.isAuthenticated()
          .then(this._fetchCurrentUserModel)
          .done(this._setFetched)
          .always(this._setFetchingComplete);
        }

      return this._fetchUserModelPromise || $.Deferred().resolve().promise();
    },

    /**
     * Helper function that fetches the current user model.
     *
     * @method _fetchCurrentUserModel
     * @return {Promise} from the fetch.
     * @private
     */
    _fetchCurrentUserModel: function() {
      return this.currentUserModel.fetch();
    },

    /**
     * Helper function that fetches the current admin user model.
     *
     * @method fetchCurrentAdminUserModel
     * @return {Promise} from the fetch.
     * @private
     */
    _fetchCurrentAdminUserModel: function() {
      return this.currentAdminUserModel.fetch();
    },

    /**
     * @method isAuthenticated
     * @return {Promise} Resolves if any user is authenticated, rejects if no users are authenticated
     */
    isAuthenticated: function() {
      var userService = this;
      return $.ajax({
          url: loginStatusExtService,
          type: 'GET'
        }).then(function(loginStatus) {
          var deferred = $.Deferred();
          if (loginStatus.status) {
            userService.set('authenticated', true);
            deferred.resolve();
          } else {
            userService.set('authenticated', false);
            deferred.reject();
          }
          return deferred.promise();
        });
    },

    /**
     * Return whether the current user has the given role
     * @method hasRole
     * @param role to check
     * @return {Boolean} whether or not current user has role
     */
    hasRole: function(role) {
      var roles = this.currentUserModel.get('roles');
      var hasRole = roles && roles.indexOf(role) !== -1;
      return hasRole;
    },

    /**
     * Reset all of the user service models and re-fetch them.
     *
     * @method resetAndFetchModels
     * @return {Promise} Returns a promise object that resolves when all models finish fetching
     */
    resetAndFetchModels: function() {
      this._reset();
      return this.fetchModels();
    },

    /**
     * Resets the user service models to an un-fetched state.  Useful when the user logs out.
     *
     * @method _reset
     * @private
     */
    _reset: function() {
      this.currentUserModel.clear();
      this.currentAdminUserModel.clear();
      this.currentDemographicsModel.clear();
      this._setModelDefaults();
      this.set('fetched', false);
      this.set('fetching', false);
      this.set('authenticated', false);
      delete this._fetchModelsPromise;
      delete this._fetchUserModelPromise;
      this.trigger('reset');
    }
  }))();
}));
